import { useEffect, useMemo, useState } from "react";
import { format, parseISO } from "date-fns";
import { it } from "date-fns/locale";
import { Calculator, FileDown, History, RotateCcw, Trash2 } from "lucide-react";
import { toast } from "sonner";

const QUESTIONS_BY_LANG = {
  it: [
    "Quanto vuole che il suo sito colpisca positivamente chi lo visita?",
    "Quanto deve essere grande e completo il sito, con diverse pagine e sezioni?",
    "Quanto desidera un design personalizzato, creato appositamente per la sua attività?",
    "Quanto vorrebbe inserire animazioni, effetti e piccoli elementi interattivi?",
    "Quanto le serve avere il sito disponibile in più lingue?",
    "Quanto supporto desidera per preparare e scrivere i testi del sito?",
    "Quanto supporto le serve per avere fotografie adatte al sito?",
    "Quanto lavoro sarà necessario per sistemare, ritagliare e migliorare le fotografie disponibili?",
    "Quanto desidera presentare nel dettaglio i suoi servizi o prodotti?",
    "Quanto desidera inserire un listino prezzi sul sito?",
    "Quanto è importante avere un catalogo dei prodotti o servizi consultabile online?",
    "Quanto desidera organizzare il catalogo in categorie, con filtri o possibilità di ricerca?",
    "Quanto le interessa permettere ai clienti di prenotare appuntamenti direttamente dal sito?",
    "Quanto desidera avere moduli di contatto per ricevere richieste dai clienti?",
    "Quanto desidera mostrare le recensioni dei suoi clienti direttamente sul sito?",
    "Quanto vorrebbe una sezione FAQ con le risposte alle domande più frequenti dei clienti?",
    "Quanto desidera collegare il sito ai social network, a WhatsApp o a Google Maps?",
    "Quanto le interessa vendere prodotti o servizi direttamente dal sito, con carrello e pagamenti online?",
    "Quanto è importante per lei migliorare la visibilità del sito su Google?",
    "Quanto desidera collegare il sito ad altri strumenti o automatizzare attività della sua azienda?",
  ],
  de: [
    "Wie stark soll Ihre Website Besucher positiv beeindrucken?",
    "Wie gross und umfangreich soll die Website sein, mit mehreren Seiten und Bereichen?",
    "Wie sehr wünschen Sie ein individuelles Design, speziell für Ihr Unternehmen gestaltet?",
    "Wie gerne möchten Sie Animationen, Effekte und kleine interaktive Elemente einbauen?",
    "Wie wichtig ist es Ihnen, die Website in mehreren Sprachen anzubieten?",
    "Wie viel Unterstützung wünschen Sie beim Vorbereiten und Schreiben der Texte?",
    "Wie viel Unterstützung brauchen Sie, um passende Fotos für die Website zu erhalten?",
    "Wie viel Aufwand ist nötig, um die vorhandenen Fotos zu bearbeiten, zuzuschneiden und zu verbessern?",
    "Wie detailliert möchten Sie Ihre Dienstleistungen oder Produkte präsentieren?",
    "Wie gerne möchten Sie eine Preisliste auf der Website veröffentlichen?",
    "Wie wichtig ist ein online abrufbarer Katalog Ihrer Produkte oder Dienstleistungen?",
    "Wie sehr möchten Sie den Katalog in Kategorien gliedern, mit Filtern oder Suchfunktion?",
    "Wie interessant ist es für Sie, dass Kunden direkt über die Website Termine buchen können?",
    "Wie sehr wünschen Sie Kontaktformulare, um Anfragen von Kunden zu erhalten?",
    "Wie gerne möchten Sie die Bewertungen Ihrer Kunden direkt auf der Website zeigen?",
    "Wie gerne hätten Sie einen FAQ-Bereich mit Antworten auf die häufigsten Kundenfragen?",
    "Wie sehr möchten Sie die Website mit sozialen Netzwerken, WhatsApp oder Google Maps verbinden?",
    "Wie interessant ist es für Sie, Produkte oder Dienstleistungen direkt über die Website zu verkaufen, mit Warenkorb und Online-Zahlung?",
    "Wie wichtig ist es Ihnen, die Sichtbarkeit der Website bei Google zu verbessern?",
    "Wie sehr möchten Sie die Website mit anderen Tools verbinden oder Abläufe in Ihrem Unternehmen automatisieren?",
  ],
  en: [
    "How strongly do you want your website to impress visitors?",
    "How large and complete should the website be, with multiple pages and sections?",
    "How much do you want a custom design created specifically for your business?",
    "How much would you like to include animations, effects and small interactive elements?",
    "How much do you need the website to be available in multiple languages?",
    "How much support do you want in preparing and writing the website copy?",
    "How much support do you need to get suitable photos for the website?",
    "How much work will be needed to edit, crop and improve the available photos?",
    "How much do you want to present your services or products in detail?",
    "How much do you want to include a price list on the website?",
    "How important is it to have an online catalogue of your products or services?",
    "How much do you want to organise the catalogue into categories, with filters or search?",
    "How interested are you in letting customers book appointments directly on the website?",
    "How much do you want contact forms to receive enquiries from customers?",
    "How much do you want to show your customers' reviews directly on the website?",
    "How much would you like an FAQ section answering customers' most common questions?",
    "How much do you want to connect the website to social networks, WhatsApp or Google Maps?",
    "How interested are you in selling products or services directly on the website, with a cart and online payments?",
    "How important is it for you to improve the website's visibility on Google?",
    "How much do you want to connect the website to other tools or automate tasks in your business?",
  ],
  fr: [
    "Dans quelle mesure souhaitez-vous que votre site impressionne positivement les visiteurs ?",
    "Quelle doit être la taille et la complétude du site, avec plusieurs pages et sections ?",
    "Dans quelle mesure souhaitez-vous un design personnalisé, créé spécialement pour votre activité ?",
    "Dans quelle mesure aimeriez-vous intégrer des animations, des effets et de petits éléments interactifs ?",
    "Dans quelle mesure avez-vous besoin que le site soit disponible en plusieurs langues ?",
    "Quel niveau d'accompagnement souhaitez-vous pour préparer et rédiger les textes du site ?",
    "Quel niveau d'accompagnement vous faut-il pour obtenir des photos adaptées au site ?",
    "Quel travail sera nécessaire pour retoucher, recadrer et améliorer les photos disponibles ?",
    "Dans quelle mesure souhaitez-vous présenter en détail vos services ou produits ?",
    "Dans quelle mesure souhaitez-vous intégrer une liste de prix sur le site ?",
    "Quelle est l'importance d'un catalogue de produits ou services consultable en ligne ?",
    "Dans quelle mesure souhaitez-vous organiser le catalogue en catégories, avec filtres ou recherche ?",
    "Dans quelle mesure souhaitez-vous permettre aux clients de réserver directement sur le site ?",
    "Dans quelle mesure souhaitez-vous des formulaires de contact pour recevoir les demandes des clients ?",
    "Dans quelle mesure souhaitez-vous afficher les avis de vos clients directement sur le site ?",
    "Dans quelle mesure aimeriez-vous une section FAQ répondant aux questions les plus fréquentes ?",
    "Dans quelle mesure souhaitez-vous relier le site aux réseaux sociaux, à WhatsApp ou à Google Maps ?",
    "Dans quelle mesure souhaitez-vous vendre des produits ou services directement sur le site, avec panier et paiement en ligne ?",
    "Quelle est l'importance pour vous d'améliorer la visibilité du site sur Google ?",
    "Dans quelle mesure souhaitez-vous relier le site à d'autres outils ou automatiser des tâches de votre entreprise ?",
  ],
};
const QUOTE_LANGS = ["it", "de", "en", "fr"];

const SCORE_OPTIONS = [0, 10, 17, 25];
const NUM_ITEMS = 20;
const MAX_TOTAL = 500;

const emptyScores = () => Array(NUM_ITEMS).fill(null);
const todayIso = () => new Date().toLocaleDateString("sv-SE");

export const AdminQuotes = ({ client, guard }) => {
  const [sheets, setSheets] = useState([]);
  const [scores, setScores] = useState(emptyScores);
  const [current, setCurrent] = useState(null);
  const [name, setName] = useState("");
  const [customer, setCustomer] = useState("");
  const [quoteDate, setQuoteDate] = useState(todayIso());
  const [showHistory, setShowHistory] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pdfBusy, setPdfBusy] = useState("");
  const [lang, setLang] = useState("it");

  const total = useMemo(() => scores.reduce((a, s) => a + (s || 0), 0), [scores]);
  const filled = scores.filter((s) => s !== null).length;

  const load = () => guard(client.get("/admin/quote-sheets").then((r) => setSheets(r.data)));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const setScore = (i, v) =>
    setScores((prev) => prev.map((s, idx) => (idx === i ? (s === v ? null : v) : s)));

  const reset = () => {
    setScores(emptyScores());
    setCurrent(null);
    setName("");
    setCustomer("");
    setQuoteDate(todayIso());
    toast.success("Tabella azzerata.");
  };

  const openSheet = (s) => {
    setCurrent(s.id);
    setName(s.name);
    setCustomer(s.customer || "");
    setQuoteDate(s.quote_date || todayIso());
    setScores(s.scores);
    setShowHistory(false);
    toast.success(`Aperta: ${s.name}`);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const save = async (asNew = false) => {
    const finalName = name.trim() || `Tabella del ${format(new Date(), "dd.MM.yyyy HH:mm")}`;
    const body = { name: finalName, customer: customer.trim(), quote_date: quoteDate || null, scores };
    if (filled < NUM_ITEMS) toast.warning(`Attenzione: ${NUM_ITEMS - filled} domande sono ancora da compilare.`);
    setSaving(true);
    try {
      await guard(
        (!asNew && current
          ? client.put(`/admin/quote-sheets/${current}`, body).then(() => "Tabella aggiornata.")
          : client.post("/admin/quote-sheets", body).then((r) => { setCurrent(r.data.id); setName(finalName); return "Tabella salvata."; })
        ).then((msg) => { toast.success(msg); load(); })
      );
    } finally {
      setSaving(false);
    }
  };

  const del = async (s) => {
    await guard(client.delete(`/admin/quote-sheets/${s.id}`).then(() => {
      setSheets((xs) => xs.filter((x) => x.id !== s.id));
      if (current === s.id) setCurrent(null);
      toast.success("Tabella eliminata.");
    }));
  };

  const downloadPdf = async (sheetId, sheetName) => {
    setPdfBusy(sheetId);
    try {
      const r = await client.get(`/admin/quote-sheets/${sheetId}/pdf`, { params: { lang }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `preventivo-${(sheetName || "alsera").slice(0, 40).replace(/\s+/g, "-").toLowerCase()}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF scaricato.");
    } catch {
      toast.error("Errore durante la generazione del PDF.");
    } finally {
      setPdfBusy("");
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-quotes">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm font-semibold text-antracite/60">
          Tabella punteggi per il preventivo sito web — 20 domande, punteggio massimo {MAX_TOTAL}. Il prezzo non compare mai in questa pagina: lo vede il cliente solo sul PDF scaricato.
        </p>
        <div className="flex gap-2">
          <button type="button" onClick={reset} className="inline-flex items-center gap-1.5 rounded-full bg-antracite/10 px-4 py-2 text-xs font-bold text-antracite/70 transition-colors hover:bg-antracite/15" data-testid="quote-reset">
            <RotateCcw className="h-3.5 w-3.5" /> Azzera
          </button>
          <button type="button" onClick={() => setShowHistory(!showHistory)} className="inline-flex items-center gap-1.5 rounded-full bg-acqua px-4 py-2 text-xs font-bold text-petrolio transition-colors hover:bg-salvia/60" data-testid="quote-history-toggle">
            <History className="h-3.5 w-3.5" /> Storico ({sheets.length})
          </button>
        </div>
      </div>

      {current && (
        <p className="rounded-2xl bg-acqua/50 px-4 py-2.5 text-xs font-bold text-petrolio" data-testid="quote-editing-banner">
          Stai modificando: {name} — usa "Salva modifiche" per aggiornarla o "Salva come nuova" per crearne una copia.
        </p>
      )}

      {showHistory && (
        <div className="card-soft space-y-3 p-6" data-testid="quote-history">
          <h3 className="text-sm font-bold uppercase tracking-[0.12em] text-antracite/60">Tabelle salvate</h3>
          {sheets.length === 0 && <p className="text-sm text-antracite/50" data-testid="quote-history-empty">Nessuna tabella salvata.</p>}
          {sheets.map((s) => (
            <div key={s.id} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-antracite/10 p-4" data-testid={`quote-sheet-${s.id.slice(0, 8)}`}>
              <div className="min-w-0">
                <p className="truncate font-bold text-antracite">{s.name}</p>
                <p className="text-xs text-antracite/50">
                  {s.customer || "—"} · {s.total}/{MAX_TOTAL} punti
                  {" · "}{format(parseISO(s.created_at), "d MMM yyyy HH:mm", { locale: it })}
                </p>
              </div>
              <div className="flex gap-2">
                <button type="button" disabled={pdfBusy === s.id} onClick={() => downloadPdf(s.id, s.name)} className="inline-flex items-center gap-1.5 rounded-full bg-elettrico/10 px-4 py-2 text-xs font-bold text-elettrico transition-colors hover:bg-elettrico/20 disabled:opacity-60" data-testid={`quote-pdf-${s.id.slice(0, 8)}`}>
                  <FileDown className="h-3.5 w-3.5" /> {pdfBusy === s.id ? "PDF…" : "PDF"}
                </button>
                <button type="button" onClick={() => openSheet(s)} className="rounded-full bg-acqua px-4 py-2 text-xs font-bold text-petrolio transition-colors hover:bg-salvia/60" data-testid={`quote-open-${s.id.slice(0, 8)}`}>Apri</button>
                <button type="button" onClick={() => del(s)} className="inline-flex items-center gap-1.5 rounded-full bg-red-50 px-4 py-2 text-xs font-bold text-red-700 transition-colors hover:bg-red-100" data-testid={`quote-delete-${s.id.slice(0, 8)}`}>
                  <Trash2 className="h-3.5 w-3.5" /> Elimina
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="card-soft space-y-4 p-6">
        <div className="grid gap-4 sm:grid-cols-4">
          <div>
            <label className="mb-1 block text-xs font-bold uppercase tracking-[0.12em] text-antracite/60">Nome della tabella</label>
            <input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} className="input-field" placeholder="es. Preventivo Rossi" data-testid="quote-name" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-bold uppercase tracking-[0.12em] text-antracite/60">Cliente</label>
            <input value={customer} onChange={(e) => setCustomer(e.target.value)} maxLength={120} className="input-field" placeholder="Nome del cliente" data-testid="quote-customer" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-bold uppercase tracking-[0.12em] text-antracite/60">Data preventivo</label>
            <input type="date" value={quoteDate} onChange={(e) => setQuoteDate(e.target.value)} className="input-field" data-testid="quote-date" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-bold uppercase tracking-[0.12em] text-antracite/60">Lingua domande e PDF</label>
            <select value={lang} onChange={(e) => setLang(e.target.value)} className="input-field" data-testid="quote-lang">
              {QUOTE_LANGS.map((l) => <option key={l} value={l}>{l.toUpperCase()}</option>)}
            </select>
          </div>
        </div>
        <p className="text-xs font-semibold text-antracite/50" data-testid="quote-progress">
          {filled} di {NUM_ITEMS} compilate — {Math.round((filled / NUM_ITEMS) * 100)}%
        </p>
      </div>

      <div className="card-soft divide-y divide-antracite/8 p-2 sm:p-4" data-testid="quote-questions">
        {QUESTIONS_BY_LANG[lang].map((q, i) => (
          <div key={i} className="flex flex-col gap-3 px-3 py-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6" data-testid={`quote-q-${i}`}>
            <p className="text-sm leading-relaxed text-antracite/85"><span className="mr-2 font-display font-extrabold text-elettrico/40">{String(i + 1).padStart(2, "0")}</span>{q}</p>
            <div className="flex shrink-0 gap-1.5">
              {SCORE_OPTIONS.map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setScore(i, v)}
                  aria-label={`Punteggio ${v} domanda ${i + 1}`}
                  className={`h-9 w-11 rounded-xl text-sm font-bold transition-colors ${
                    scores[i] === v ? "bg-petrolio text-avorio" : scores[i] === null ? "bg-antracite/5 text-antracite/50 hover:bg-acqua" : "bg-acqua/60 text-petrolio hover:bg-acqua"
                  }`}
                  data-testid={`quote-q-${i}-score-${v}`}
                >
                  {v}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="card-soft sticky bottom-4 border-2 border-petrolio/15 p-5 shadow-xl shadow-petrolio/10" data-testid="quote-actions">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm font-bold text-antracite" data-testid="quote-total">
            Totale punteggio: <span className="font-display text-lg font-extrabold text-petrolio">{total}</span> <span className="text-xs font-bold text-antracite/40">/ {MAX_TOTAL}</span>
            <span className="ml-2 hidden text-xs font-semibold text-antracite/45 sm:inline">— il prezzo è calcolato solo nel PDF</span>
          </p>
          <div className="flex flex-wrap gap-2">
            <button type="button" disabled={saving} onClick={() => save(false)} className="btn-primary !px-5 !py-2.5 !text-xs disabled:opacity-60" data-testid="quote-save">
              <Calculator className="h-4 w-4" /> {current ? "Salva modifiche" : "Salva tabella"}
            </button>
            {current && (
              <>
                <button type="button" disabled={saving} onClick={() => save(true)} className="rounded-full bg-acqua px-5 py-2.5 text-xs font-bold text-petrolio transition-colors hover:bg-salvia/60 disabled:opacity-60" data-testid="quote-save-new">
                  Salva come nuova
                </button>
                <button type="button" disabled={pdfBusy === current} onClick={() => downloadPdf(current, name)} className="inline-flex items-center gap-1.5 rounded-full bg-elettrico px-5 py-2.5 text-xs font-bold text-white transition-colors hover:bg-elettrico/85 disabled:opacity-60" data-testid="quote-pdf-current">
                  <FileDown className="h-4 w-4" /> {pdfBusy === current ? "PDF…" : "Scarica PDF"}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
